/**
 * Import Manager
 *
 * Handles importing .phrasewright format files with validation and conflict resolution.
 * Supports free tier to cloud migration and data validation.
 */

import type { StorageFile, IStorageAdapter } from '../adapters/types';

export interface ImportOptions {
  overwriteExisting?: boolean;
  skipDuplicates?: boolean;
  validateContent?: boolean;
  preserveIds?: boolean;
  conflictResolution?: 'skip' | 'overwrite' | 'rename' | 'merge';
}

export interface ImportResult {
  success: boolean;
  importedFiles: number;
  skippedFiles: number;
  conflicts: ImportConflict[];
  errors: ImportError[];
  duration: number;
}

export interface ImportConflict {
  existingFile: StorageFile;
  importFile: Partial<StorageFile>;
  conflictType: 'name' | 'id' | 'content';
  resolution: 'skipped' | 'overwritten' | 'renamed' | 'merged';
  newName?: string;
}

export interface ImportError {
  file: Partial<StorageFile>;
  error: string;
  code:
    | 'VALIDATION_FAILED'
    | 'QUOTA_EXCEEDED'
    | 'UNSUPPORTED_FORMAT'
    | 'UNKNOWN';
}

export interface ImportValidation {
  isValid: boolean;
  version: string;
  fileCount: number;
  estimatedSize: number;
  supportedFormat: boolean;
  issues: string[];
}

export class ImportManager {
  constructor(private adapter: IStorageAdapter) {}

  /**
   * Validate import file before processing
   */
  async validateImportFile(file: File): Promise<ImportValidation> {
    const validation: ImportValidation = {
      isValid: false,
      version: '',
      fileCount: 0,
      estimatedSize: file.size,
      supportedFormat: false,
      issues: [],
    };

    try {
      // Check file extension
      if (
        !file.name.endsWith('.phrasewright') &&
        !file.name.endsWith('.json')
      ) {
        validation.issues.push(
          'Unsupported file format. Only .phrasewright files are supported.'
        );
        return validation;
      }

      const text = await file.text();
      let data: any;

      try {
        data = JSON.parse(text);
      } catch {
        validation.issues.push('Invalid JSON format');
        return validation;
      }

      // Validate structure
      if (!data.version) {
        validation.issues.push('Missing version information');
      } else {
        validation.version = data.version;
      }

      if (!data.files || !Array.isArray(data.files)) {
        validation.issues.push('Missing or invalid files array');
        return validation;
      }

      validation.fileCount = data.files.length;
      validation.supportedFormat = true;

      // Validate individual files
      for (const [index, fileData] of data.files.entries()) {
        const fileIssues = this.validateFileData(fileData, index);
        validation.issues.push(...fileIssues);
      }

      // Check if this looks like a free tier export
      if (data.freeTierExport) {
        validation.issues.push(
          'Free tier export detected - ensure quota limits are considered'
        );
      }

      validation.isValid = validation.issues.length === 0;
      return validation;
    } catch (error) {
      validation.issues.push(
        `Failed to read file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      return validation;
    }
  }

  /**
   * Import files from .phrasewright format
   */
  async importFile(
    file: File,
    options: ImportOptions = {}
  ): Promise<ImportResult> {
    const startTime = Date.now();
    const result: ImportResult = {
      success: false,
      importedFiles: 0,
      skippedFiles: 0,
      conflicts: [],
      errors: [],
      duration: 0,
    };

    try {
      // Validate file first
      const validation = await this.validateImportFile(file);
      if (!validation.isValid) {
        result.errors.push({
          file: { name: file.name },
          error: `Validation failed: ${validation.issues.join(', ')}`,
          code: 'VALIDATION_FAILED',
        });
        result.duration = Date.now() - startTime;
        return result;
      }

      const text = await file.text();
      const data = JSON.parse(text);

      // Get existing files for conflict detection
      const existingFiles = await this.adapter.getFiles();
      const existingByName = new Map(existingFiles.map((f) => [f.name, f]));
      const existingById = new Map(existingFiles.map((f) => [f.id, f]));

      // Process each file
      for (const fileData of data.files) {
        try {
          const processResult = await this.processImportFile(
            fileData,
            existingByName,
            existingById,
            options
          );

          if (processResult.imported) {
            result.importedFiles++;
          } else {
            result.skippedFiles++;
          }

          if (processResult.conflict) {
            result.conflicts.push(processResult.conflict);
          }
        } catch (error) {
          result.errors.push({
            file: fileData,
            error: error instanceof Error ? error.message : 'Unknown error',
            code: 'UNKNOWN',
          });
        }
      }

      result.success = result.errors.length === 0;
      result.duration = Date.now() - startTime;
      return result;
    } catch (error) {
      result.errors.push({
        file: { name: file.name },
        error: `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        code: 'UNKNOWN',
      });
      result.duration = Date.now() - startTime;
      return result;
    }
  }

  /**
   * Preview import conflicts before actual import
   */
  async previewImport(file: File): Promise<ImportConflict[]> {
    try {
      const validation = await this.validateImportFile(file);
      if (!validation.isValid) {
        return [];
      }

      const text = await file.text();
      const data = JSON.parse(text);
      const existingFiles = await this.adapter.getFiles();

      const conflicts: ImportConflict[] = [];
      const existingByName = new Map(existingFiles.map((f) => [f.name, f]));
      const existingById = new Map(existingFiles.map((f) => [f.id, f]));

      for (const fileData of data.files) {
        const nameConflict = existingByName.get(fileData.name);
        const idConflict = existingById.get(fileData.id);

        if (nameConflict) {
          conflicts.push({
            existingFile: nameConflict,
            importFile: fileData,
            conflictType: 'name',
            resolution: 'skipped',
          });
        }

        if (idConflict && idConflict !== nameConflict) {
          conflicts.push({
            existingFile: idConflict,
            importFile: fileData,
            conflictType: 'id',
            resolution: 'skipped',
          });
        }
      }

      return conflicts;
    } catch {
      return [];
    }
  }

  /**
   * Export current data for migration
   */
  async exportForMigration(): Promise<Blob> {
    const files = await this.adapter.getFiles();

    const exportData = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      application: 'Phrasewright',
      migrationExport: true,
      files: files.map((file) => ({
        id: file.id,
        name: file.name,
        content: file.content,
        type: file.type,
        tags: file.tags,
        metadata: file.metadata,
        createdAt: file.createdAt,
        updatedAt: file.updatedAt,
        revision: file.revision,
      })),
    };

    const jsonStr = JSON.stringify(exportData, null, 2);
    return new Blob([jsonStr], { type: 'application/json' });
  }

  /**
   * Process a single import file with conflict resolution
   */
  private async processImportFile(
    fileData: any,
    existingByName: Map<string, StorageFile>,
    existingById: Map<string, StorageFile>,
    options: ImportOptions
  ): Promise<{ imported: boolean; conflict?: ImportConflict }> {
    const conflictResolution = options.conflictResolution || 'skip';

    // Check for name conflict
    const nameConflict = existingByName.get(fileData.name);
    if (nameConflict) {
      const conflict: ImportConflict = {
        existingFile: nameConflict,
        importFile: fileData,
        conflictType: 'name',
        resolution: 'skipped',
      };

      switch (conflictResolution) {
        case 'skip':
          return { imported: false, conflict };

        case 'overwrite':
          await this.adapter.updateFile(nameConflict.id, {
            content: fileData.content,
            tags: fileData.tags || [],
            metadata: fileData.metadata,
          });
          conflict.resolution = 'overwritten';
          return { imported: true, conflict };

        case 'rename':
          const newName = this.generateUniqueName(
            fileData.name,
            existingByName
          );
          await this.adapter.createFile({
            name: newName,
            content: fileData.content,
            type: fileData.type,
            tags: fileData.tags || [],
            metadata: fileData.metadata,
          });
          conflict.resolution = 'renamed';
          conflict.newName = newName;
          return { imported: true, conflict };

        case 'merge':
          // Simple merge: append content
          const mergedContent =
            nameConflict.content + '\n\n---\n\n' + fileData.content;
          await this.adapter.updateFile(nameConflict.id, {
            content: mergedContent,
            tags: [
              ...new Set([...nameConflict.tags, ...(fileData.tags || [])]),
            ],
          });
          conflict.resolution = 'merged';
          return { imported: true, conflict };
      }
    }

    // Check for ID conflict (when preserving IDs)
    if (options.preserveIds && existingById.has(fileData.id)) {
      const conflict: ImportConflict = {
        existingFile: existingById.get(fileData.id)!,
        importFile: fileData,
        conflictType: 'id',
        resolution: 'skipped',
      };
      return { imported: false, conflict };
    }

    // No conflicts, create new file
    const createData: any = {
      name: fileData.name,
      content: fileData.content,
      type: fileData.type,
      tags: fileData.tags || [],
      metadata: fileData.metadata,
    };

    // Preserve ID if requested and no conflict
    if (options.preserveIds && fileData.id) {
      createData.id = fileData.id;
    }

    await this.adapter.createFile(createData);
    return { imported: true };
  }

  /**
   * Validate individual file data
   */
  private validateFileData(fileData: any, index: number): string[] {
    const issues: string[] = [];
    const prefix = `File ${index + 1}:`;

    if (!fileData.name || typeof fileData.name !== 'string') {
      issues.push(`${prefix} Missing or invalid name`);
    }

    if (!fileData.content || typeof fileData.content !== 'string') {
      issues.push(`${prefix} Missing or invalid content`);
    }

    if (!fileData.type || !['document', 'phrase'].includes(fileData.type)) {
      issues.push(
        `${prefix} Missing or invalid type (must be 'document' or 'phrase')`
      );
    }

    if (fileData.tags && !Array.isArray(fileData.tags)) {
      issues.push(`${prefix} Invalid tags (must be array)`);
    }

    if (!fileData.createdAt || !fileData.updatedAt) {
      issues.push(`${prefix} Missing timestamp information`);
    }

    return issues;
  }

  /**
   * Generate unique name for renamed files
   */
  private generateUniqueName(
    baseName: string,
    existingByName: Map<string, StorageFile>
  ): string {
    let counter = 1;
    let newName = `${baseName} (${counter})`;

    while (existingByName.has(newName)) {
      counter++;
      newName = `${baseName} (${counter})`;
    }

    return newName;
  }

  /**
   * Check if import is from free tier export
   */
  async isFreeTierExport(file: File): Promise<boolean> {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      return data.freeTierExport === true;
    } catch {
      return false;
    }
  }

  /**
   * Estimate import size and quota requirements
   */
  async estimateImportQuota(file: File): Promise<{
    fileCount: number;
    documentCount: number;
    phraseCount: number;
    estimatedSize: number;
  }> {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.files || !Array.isArray(data.files)) {
        return {
          fileCount: 0,
          documentCount: 0,
          phraseCount: 0,
          estimatedSize: 0,
        };
      }

      const documentCount = data.files.filter(
        (f: any) => f.type === 'document'
      ).length;
      const phraseCount = data.files.filter(
        (f: any) => f.type === 'phrase'
      ).length;

      return {
        fileCount: data.files.length,
        documentCount,
        phraseCount,
        estimatedSize: file.size,
      };
    } catch {
      return {
        fileCount: 0,
        documentCount: 0,
        phraseCount: 0,
        estimatedSize: 0,
      };
    }
  }
}
